import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Save, X, Trash2, Building2, Search } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';

interface Estate {
  id: number;
  name: string;
  nameEn?: string;
  region: 'NT' | 'KL' | 'HK';
  district?: string;
  area?: string;
  estateType?: string;
  managementCompany?: string;
  minOrderAmount: number;
  deliveryDay?: string;
  supportsDelivery?: number;
  supportsPickup?: number;
  pickupLocation?: string;
  pickupTimeNote?: string;
  isActive: number;
  sortOrder: number;
}

interface CustomEstate {
  id: number;
  name: string;
  region: 'HK' | 'KL' | 'NT';
  district?: string;
  area?: string;
  estateType?: string;
  minOrderAmount: number;
  isActive: number | boolean;
  sortOrder: number;
}

const regionOptions = [
  { value: 'NT', label: '新界' },
  { value: 'KL', label: '九龍' },
  { value: 'HK', label: '香港島' },
];

function formatEpochDate(ts: string | number | null | undefined): string {
  if (ts === null || ts === undefined || ts === '') return '-';
  const num = Number(ts);
  if (!Number.isFinite(num)) return '-';
  // D1 以 unixepoch()（秒）儲存；JS Date 需要毫秒
  const ms = num < 1e12 ? num * 1000 : num;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('zh-HK');
}

function deriveRegionFromDistrict(district: string): 'NT' | 'KL' | 'HK' {
  const d = district.trim();
  if (['中西區', '灣仔區', '東區', '南區'].includes(d)) return 'HK';
  if (['油尖旺區', '深水埗區', '九龍城區', '黃大仙區', '觀塘區'].includes(d)) return 'KL';
  return 'NT';
}

interface EstateRequestEntry {
  phone: string;
  name?: string;
  createdAt: string;
  requesterType: 'consumer' | 'sen_youth' | 'sen_parent';
}

interface EstateRequestGroup {
  estateName: string;
  district: string;
  requesterType: 'consumer' | 'sen_youth' | 'sen_parent';
  count: number;
  latestAt: string;
  entries: EstateRequestEntry[];
}

const REQUESTER_TYPE_OPTIONS: { value: EstateRequestGroup['requesterType'] | 'all'; label: string }[] = [
  { value: 'all', label: '全部登記類型' },
  { value: 'consumer', label: '消費者 — 希望我的屋苑增設鄰里店' },
  { value: 'sen_youth', label: 'SEN 青年本人 — 希望開設鄰里店' },
  { value: 'sen_parent', label: 'SEN 青年家長 — 希望開設鄰里店' },
];

function requesterTypeLabel(type?: EstateRequestGroup['requesterType']): string {
  switch (type) {
    case 'sen_youth': return 'SEN 青年本人';
    case 'sen_parent': return 'SEN 青年家長';
    case 'consumer':
    default: return '消費者';
  }
}

const AdminEstates: React.FC = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWriteEstates = can(PERMISSIONS.ESTATES_WRITE);
  const [estates, setEstates] = useState<Estate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const emptyForm = {
    name: '',
    nameEn: '',
    region: 'NT' as 'NT' | 'KL' | 'HK',
    district: '',
    area: '',
    estateType: '',
    managementCompany: '',
    minOrderAmount: '',
    deliveryDay: '',
    supportsDelivery: true,
    supportsPickup: false,
    pickupLocation: '',
    pickupTimeNote: '',
    isActive: true,
    sortOrder: '0',
  };
  const [formData, setFormData] = useState(emptyForm);

  // 「需求池」tab 狀態
  const [activeTab, setActiveTab] = useState<'estates' | 'custom' | 'demand'>('estates');
  const [demandGroups, setDemandGroups] = useState<EstateRequestGroup[]>([]);
  const [demandLoading, setDemandLoading] = useState(false);
  const [demandError, setDemandError] = useState('');
  const [expandedEstate, setExpandedEstate] = useState<string | null>(null);
  const [demandTypeFilter, setDemandTypeFilter] = useState<'all' | 'consumer' | 'sen_youth' | 'sen_parent'>('all');

  const filteredDemandGroups = useMemo(() => {
    if (demandTypeFilter === 'all') return demandGroups;
    return demandGroups.filter(g => g.requesterType === demandTypeFilter);
  }, [demandGroups, demandTypeFilter]);

  // 「自選屋苑」tab 狀態
  const [customEstates, setCustomEstates] = useState<CustomEstate[]>([]);
  const [customLoading, setCustomLoading] = useState(false);
  const [customError, setCustomError] = useState('');
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customEditingId, setCustomEditingId] = useState<number | null>(null);
  const emptyCustomForm = {
    name: '',
    region: 'NT' as 'NT' | 'KL' | 'HK',
    district: '',
    area: '',
    estateType: '',
    minOrderAmount: '',
    isActive: true,
    sortOrder: '0',
  };
  const [customFormData, setCustomFormData] = useState(emptyCustomForm);

  // 自選屋苑篩選與分頁
  const [customRegionFilter, setCustomRegionFilter] = useState<'all' | 'HK' | 'KL' | 'NT'>('all');
  const [customDistrictFilter, setCustomDistrictFilter] = useState<string>('all');
  const [customAreaFilter, setCustomAreaFilter] = useState<string>('all');
  const [customTypeFilter, setCustomTypeFilter] = useState<string>('all');
  const [customSearch, setCustomSearch] = useState('');
  const [customPage, setCustomPage] = useState(1);
  const CUSTOM_PER_PAGE = 20;

  // 服務屋苑篩選與分頁
  const [estateRegionFilter, setEstateRegionFilter] = useState<'all' | 'HK' | 'KL' | 'NT'>('all');
  const [estateDistrictFilter, setEstateDistrictFilter] = useState<string>('all');
  const [estateAreaFilter, setEstateAreaFilter] = useState<string>('all');
  const [estateTypeFilter, setEstateTypeFilter] = useState<string>('all');
  const [estateSearch, setEstateSearch] = useState('');
  const [estatePage, setEstatePage] = useState(1);
  const ESTATES_PER_PAGE = 20;

  useEffect(() => {
    fetchEstates();
  }, []);

  useEffect(() => {
    if (activeTab === 'demand') {
      fetchDemand();
    }
    if (activeTab === 'custom') {
      fetchCustomEstates();
    }
  }, [activeTab]);

  // 篩選條件改變時重置分頁
  useEffect(() => {
    setCustomPage(1);
  }, [customRegionFilter, customDistrictFilter, customAreaFilter, customTypeFilter, customSearch]);

  useEffect(() => {
    setEstatePage(1);
  }, [estateRegionFilter, estateDistrictFilter, estateAreaFilter, estateTypeFilter, estateSearch]);

  const fetchDemand = async () => {
    try {
      setDemandLoading(true);
      setDemandError('');
      const res = await apiFetch('/api/public/admin/estate-requests');
      if (res.ok) {
        const data = await res.json();
        setDemandGroups(Array.isArray(data) ? data : []);
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        setDemandError('獲取屋苑需求失敗');
      }
    } catch {
      setDemandError('獲取屋苑需求失敗');
    } finally {
      setDemandLoading(false);
    }
  };

  const fetchCustomEstates = async () => {
    try {
      setCustomLoading(true);
      setCustomError('');
      const res = await apiFetch('/api/public/admin/custom-estates');
      if (res.ok) {
        setCustomEstates(await res.json());
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        setCustomError('獲取自選屋苑失敗');
      }
    } catch {
      setCustomError('獲取自選屋苑失敗');
    } finally {
      setCustomLoading(false);
    }
  };

  const fetchEstates = async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/public/admin/service-estates');
      if (res.ok) {
        setEstates(await res.json());
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        setError('獲取屋苑列表失敗');
      }
    } catch {
      setError('獲取屋苑列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 4000);
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      setError('請填寫屋苑名稱');
      return;
    }
    const payload = {
      name: formData.name,
      name_en: formData.nameEn || undefined,
      district: formData.district || undefined,
      area: formData.area || undefined,
      estate_type: formData.estateType || undefined,
      management_company: formData.managementCompany || undefined,
      min_order_amount: Number(formData.minOrderAmount) || 0,
      delivery_day: formData.deliveryDay || undefined,
      supports_delivery: formData.supportsDelivery,
      supports_pickup: formData.supportsPickup,
      pickup_location: formData.pickupLocation || undefined,
      pickup_time_note: formData.pickupTimeNote || undefined,
      is_active: formData.isActive,
      sort_order: Number(formData.sortOrder) || 0,
    };
    try {
      const res = await apiFetch(
        editingId ? `/api/public/admin/service-estates/${editingId}` : '/api/public/admin/service-estates',
        {
          method: editingId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (res.ok) {
        showMessage(editingId ? '屋苑已更新' : '屋苑已新增');
        resetForm();
        fetchEstates();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '儲存失敗');
      }
    } catch {
      setError('儲存失敗');
    }
  };

  const handleEdit = (estate: Estate) => {
    setEditingId(estate.id);
    setFormData({
      name: estate.name,
      nameEn: estate.nameEn || '',
      region: estate.region,
      district: estate.district || '',
      area: estate.area || '',
      estateType: estate.estateType || '',
      managementCompany: estate.managementCompany || '',
      minOrderAmount: String(estate.minOrderAmount || 0),
      deliveryDay: estate.deliveryDay || '',
      supportsDelivery: estate.supportsDelivery === 1,
      supportsPickup: estate.supportsPickup === 1,
      pickupLocation: estate.pickupLocation || '',
      pickupTimeNote: estate.pickupTimeNote || '',
      isActive: estate.isActive === 1,
      sortOrder: String(estate.sortOrder || 0),
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('確定要刪除此屋苑嗎？相關用戶的屋苑權限也會一併移除。')) return;
    try {
      const res = await apiFetch(`/api/public/admin/service-estates/${id}`, { method: 'DELETE' });
      if (res.ok) {
        showMessage('屋苑已刪除');
        fetchEstates();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '刪除失敗');
      }
    } catch {
      setError('刪除失敗');
    }
  };

  const resetCustomForm = () => {
    setCustomFormData(emptyCustomForm);
    setCustomEditingId(null);
    setShowCustomForm(false);
  };

  const handleCustomSubmit = async () => {
    if (!customFormData.name) {
      setCustomError('請填寫屋苑名稱');
      return;
    }
    const payload = {
      name: customFormData.name,
      region: customFormData.region,
      district: customFormData.district || undefined,
      area: customFormData.area || undefined,
      estateType: customFormData.estateType || undefined,
      minOrderAmount: Number(customFormData.minOrderAmount) || 0,
      isActive: customFormData.isActive,
      sortOrder: Number(customFormData.sortOrder) || 0,
    };
    try {
      const res = await apiFetch(
        customEditingId ? `/api/public/admin/custom-estates/${customEditingId}` : '/api/public/admin/custom-estates',
        {
          method: customEditingId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (res.ok) {
        showMessage(customEditingId ? '自選屋苑已更新' : '自選屋苑已新增');
        resetCustomForm();
        fetchCustomEstates();
      } else {
        const data = await res.json().catch(() => ({}));
        setCustomError(data.error || '儲存失敗');
      }
    } catch {
      setCustomError('儲存失敗');
    }
  };

  const filteredCustomEstates = useMemo(() => {
    const term = customSearch.trim().toLowerCase();
    return customEstates
      .filter((row) => {
        const matchesRegion = customRegionFilter === 'all' || row.region === customRegionFilter;
        const matchesDistrict = customDistrictFilter === 'all' || row.district === customDistrictFilter;
        const matchesArea = customAreaFilter === 'all' || row.area === customAreaFilter;
        const matchesType = customTypeFilter === 'all' || row.estateType === customTypeFilter;
        const matchesSearch = !term ||
          row.name.toLowerCase().includes(term) ||
          (row.district && row.district.toLowerCase().includes(term)) ||
          (row.area && row.area.toLowerCase().includes(term)) ||
          (row.estateType && row.estateType.toLowerCase().includes(term));
        return matchesRegion && matchesDistrict && matchesArea && matchesType && matchesSearch;
      })
      .sort((a, b) => {
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        return a.name.localeCompare(b.name, 'zh-HK');
      });
  }, [customEstates, customRegionFilter, customDistrictFilter, customAreaFilter, customTypeFilter, customSearch]);

  const customTotalPages = Math.max(1, Math.ceil(filteredCustomEstates.length / CUSTOM_PER_PAGE));
  const paginatedCustomEstates = useMemo(() => {
    const start = (customPage - 1) * CUSTOM_PER_PAGE;
    return filteredCustomEstates.slice(start, start + CUSTOM_PER_PAGE);
  }, [filteredCustomEstates, customPage]);

  const customDistrictOptions = useMemo(() => {
    const source = customRegionFilter === 'all' ? customEstates : customEstates.filter((r) => r.region === customRegionFilter);
    const districts = new Set<string>();
    source.forEach((row) => { if (row.district) districts.add(row.district); });
    return ['全部', ...Array.from(districts).sort()];
  }, [customEstates, customRegionFilter]);

  const customAreaOptions = useMemo(() => {
    let source = customEstates;
    if (customRegionFilter !== 'all') source = source.filter((r) => r.region === customRegionFilter);
    if (customDistrictFilter !== 'all') source = source.filter((r) => r.district === customDistrictFilter);
    const areas = new Set<string>();
    source.forEach((row) => { if (row.area) areas.add(row.area); });
    return ['全部', ...Array.from(areas).sort()];
  }, [customEstates, customRegionFilter, customDistrictFilter]);

  const customTypeOptions = useMemo(() => {
    const types = new Set<string>();
    customEstates.forEach((row) => { if (row.estateType) types.add(row.estateType); });
    return ['全部', ...Array.from(types).sort()];
  }, [customEstates]);

  const filteredEstates = useMemo(() => {
    const term = estateSearch.trim().toLowerCase();
    return estates
      .filter((row) => {
        const matchesRegion = estateRegionFilter === 'all' || row.region === estateRegionFilter;
        const matchesDistrict = estateDistrictFilter === 'all' || row.district === estateDistrictFilter;
        const matchesArea = estateAreaFilter === 'all' || row.area === estateAreaFilter;
        const matchesType = estateTypeFilter === 'all' || row.estateType === estateTypeFilter;
        const matchesSearch = !term ||
          row.name.toLowerCase().includes(term) ||
          (row.district && row.district.toLowerCase().includes(term)) ||
          (row.area && row.area.toLowerCase().includes(term)) ||
          (row.estateType && row.estateType.toLowerCase().includes(term)) ||
          (row.managementCompany && row.managementCompany.toLowerCase().includes(term));
        return matchesRegion && matchesDistrict && matchesArea && matchesType && matchesSearch;
      })
      .sort((a, b) => {
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        return a.name.localeCompare(b.name, 'zh-HK');
      });
  }, [estates, estateRegionFilter, estateDistrictFilter, estateAreaFilter, estateTypeFilter, estateSearch]);

  const estateTotalPages = Math.max(1, Math.ceil(filteredEstates.length / ESTATES_PER_PAGE));
  const paginatedEstates = useMemo(() => {
    const start = (estatePage - 1) * ESTATES_PER_PAGE;
    return filteredEstates.slice(start, start + ESTATES_PER_PAGE);
  }, [filteredEstates, estatePage]);

  const estateDistrictOptions = useMemo(() => {
    const source = estateRegionFilter === 'all' ? estates : estates.filter((r) => r.region === estateRegionFilter);
    const districts = new Set<string>();
    source.forEach((row) => { if (row.district) districts.add(row.district); });
    return ['全部', ...Array.from(districts).sort()];
  }, [estates, estateRegionFilter]);

  const estateAreaOptions = useMemo(() => {
    let source = estates;
    if (estateRegionFilter !== 'all') source = source.filter((r) => r.region === estateRegionFilter);
    if (estateDistrictFilter !== 'all') source = source.filter((r) => r.district === estateDistrictFilter);
    const areas = new Set<string>();
    source.forEach((row) => { if (row.area) areas.add(row.area); });
    return ['全部', ...Array.from(areas).sort()];
  }, [estates, estateRegionFilter, estateDistrictFilter]);

  const estateTypeOptionsList = useMemo(() => {
    const types = new Set<string>();
    estates.forEach((row) => { if (row.estateType) types.add(row.estateType); });
    return ['全部', ...Array.from(types).sort()];
  }, [estates]);

  const handleCustomEdit = (estate: CustomEstate) => {
    setCustomEditingId(estate.id);
    setCustomFormData({
      name: estate.name,
      region: estate.region as 'NT' | 'KL' | 'HK',
      district: estate.district || '',
      area: estate.area || '',
      estateType: estate.estateType || '',
      minOrderAmount: String(estate.minOrderAmount || 0),
      isActive: estate.isActive === 1 || estate.isActive === true,
      sortOrder: String(estate.sortOrder || 0),
    });
    setShowCustomForm(true);
  };

  const handleCustomDelete = async (id: number) => {
    if (!confirm('確定要刪除此自選屋苑嗎？')) return;
    try {
      const res = await apiFetch(`/api/public/admin/custom-estates/${id}`, { method: 'DELETE' });
      if (res.ok) {
        showMessage('自選屋苑已刪除');
        fetchCustomEstates();
      } else {
        const data = await res.json().catch(() => ({}));
        setCustomError(data.error || '刪除失敗');
      }
    } catch {
      setCustomError('刪除失敗');
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Building2 className="text-orange-600" />
              屋苑管理
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">管理可送貨屋苑及管理公司</p>
          </div>
          {activeTab === 'estates' && canWriteEstates && (
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg transition"
            >
              <Plus size={20} /> 新增屋苑
            </button>
          )}
          {activeTab === 'custom' && canWriteEstates && (
            <button
              onClick={() => { resetCustomForm(); setShowCustomForm(true); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg transition"
            >
              <Plus size={20} /> 新增自選屋苑
            </button>
          )}
        </div>

        {/* ===== Tab 切換 ===== */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="flex space-x-2 md:space-x-8 overflow-x-auto" aria-label="分頁">
            {[
              { id: 'estates' as const, label: '服務屋苑' },
              { id: 'custom' as const, label: '自選屋苑' },
              { id: 'demand' as const, label: '需求池' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={'py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ' + (
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'estates' && (
        <>
        {success && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {showForm && (
          <div className="mb-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4">
              {editingId ? '編輯屋苑' : '新增屋苑'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">屋苑名稱 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="例如：Yoho Midtown"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">英文名稱</label>
                <input
                  type="text"
                  value={formData.nameEn}
                  onChange={e => setFormData(prev => ({ ...prev, nameEn: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">行政區域 *</label>
                <input
                  type="text"
                  value={formData.district}
                  onChange={e => setFormData(prev => ({ ...prev, district: e.target.value, region: deriveRegionFromDistrict(e.target.value) }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="例如：北區、元朗區"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">港九新界（由行政區自動帶出）</label>
                <select
                  value={formData.region}
                  disabled
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 outline-none cursor-not-allowed"
                >
                  {regionOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">具體地區</label>
                <input
                  type="text"
                  value={formData.area}
                  onChange={e => setFormData(prev => ({ ...prev, area: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="例如：粉嶺、百和路"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">屋苑類型</label>
                <input
                  type="text"
                  value={formData.estateType}
                  onChange={e => setFormData(prev => ({ ...prev, estateType: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="例如：公屋、居屋、私人屋苑"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">管理公司</label>
                <input
                  type="text"
                  value={formData.managementCompany}
                  onChange={e => setFormData(prev => ({ ...prev, managementCompany: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="例如：啟勝"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">最低消費（HK$）</label>
                <input
                  type="number"
                  min={0}
                  value={formData.minOrderAmount}
                  onChange={e => setFormData(prev => ({ ...prev, minOrderAmount: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="0 代表使用地區預設"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">可送貨日</label>
                <input
                  type="text"
                  value={formData.deliveryDay}
                  onChange={e => setFormData(prev => ({ ...prev, deliveryDay: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="例如：星期二、四、六"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序</label>
                <input
                  type="number"
                  value={formData.sortOrder}
                  onChange={e => setFormData(prev => ({ ...prev, sortOrder: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="flex items-center gap-3 md:col-span-1">
                <input
                  id="estate-supports-delivery"
                  type="checkbox"
                  checked={formData.supportsDelivery}
                  onChange={e => setFormData(prev => ({ ...prev, supportsDelivery: e.target.checked }))}
                  className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500"
                />
                <label htmlFor="estate-supports-delivery" className="text-sm font-medium text-gray-700 dark:text-gray-300">支援送貨上門</label>
              </div>
              <div className="flex items-center gap-3 md:col-span-1">
                <input
                  id="estate-supports-pickup"
                  type="checkbox"
                  checked={formData.supportsPickup}
                  onChange={e => setFormData(prev => ({ ...prev, supportsPickup: e.target.checked }))}
                  className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500"
                />
                <label htmlFor="estate-supports-pickup" className="text-sm font-medium text-gray-700 dark:text-gray-300">支援自取</label>
              </div>
              {formData.supportsPickup && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">自取點地址</label>
                    <input
                      type="text"
                      value={formData.pickupLocation}
                      onChange={e => setFormData(prev => ({ ...prev, pickupLocation: e.target.value }))}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="例如：大學山會所地下"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">自取時段提示</label>
                    <input
                      type="text"
                      value={formData.pickupTimeNote}
                      onChange={e => setFormData(prev => ({ ...prev, pickupTimeNote: e.target.value }))}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="例如：每日 15:00-19:00"
                    />
                  </div>
                </>
              )}
              <div className="flex items-center gap-2 md:col-span-2">
                <input
                  id="estate-active"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={e => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                />
                <label htmlFor="estate-active" className="text-sm text-gray-700 dark:text-gray-300">啟用</label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSubmit}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg transition"
              >
                <Save size={18} /> 儲存
              </button>
              <button
                onClick={resetForm}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-bold rounded-lg transition"
              >
                <X size={18} /> 取消
              </button>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 flex-1">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">港九新界</label>
                <select
                  value={estateRegionFilter}
                  onChange={(e) => {
                    setEstateRegionFilter(e.target.value as 'all' | 'HK' | 'KL' | 'NT');
                    setEstateDistrictFilter('all');
                    setEstateAreaFilter('all');
                  }}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="all">全部</option>
                  {regionOptions.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">行政區域</label>
                <select
                  value={estateDistrictFilter}
                  onChange={(e) => {
                    setEstateDistrictFilter(e.target.value);
                    setEstateAreaFilter('all');
                  }}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  {estateDistrictOptions.map((d) => (
                    <option key={d} value={d === '全部' ? 'all' : d}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">具體地區</label>
                <select
                  value={estateAreaFilter}
                  onChange={(e) => setEstateAreaFilter(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  {estateAreaOptions.map((a) => (
                    <option key={a} value={a === '全部' ? 'all' : a}>{a}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">屋苑類型</label>
                <select
                  value={estateTypeFilter}
                  onChange={(e) => setEstateTypeFilter(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  {estateTypeOptionsList.map((t) => (
                    <option key={t} value={t === '全部' ? 'all' : t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">搜尋屋苑</label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="輸入屋苑名稱"
                    value={estateSearch}
                    onChange={(e) => setEstateSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-gray-600 dark:text-gray-400">載入中...</p>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">名稱</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">行政區域</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">具體地區</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">類型</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">管理公司</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">最低消費</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">服務方式</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">狀態</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedEstates.map(estate => (
                    <tr key={estate.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">{estate.name}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{estate.district || '-'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{estate.area || '-'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{estate.estateType || '-'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{estate.managementCompany || '-'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {estate.minOrderAmount > 0 ? `HK$${estate.minOrderAmount}` : '地區預設'}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-xs">
                        <div className="flex flex-wrap gap-1">
                          {estate.supportsDelivery === 1 && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">送貨</span>}
                          {estate.supportsPickup === 1 && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">自取</span>}
                          {estate.supportsDelivery !== 1 && estate.supportsPickup !== 1 && <span>-</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${estate.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                          {estate.isActive ? '啟用' : '停用'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canWriteEstates ? (
                          <div className="inline-flex gap-2">
                            <button
                              onClick={() => handleEdit(estate)}
                              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition"
                              title="編輯"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={() => handleDelete(estate.id)}
                              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
                              title="刪除"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                  {paginatedEstates.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        暫時沒有屋苑，請按「新增屋苑」建立。
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {filteredEstates.length > ESTATES_PER_PAGE && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-center items-center gap-3">
                <button
                  disabled={estatePage <= 1}
                  onClick={() => setEstatePage((p) => p - 1)}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                >
                  上一頁
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  第 {estatePage} / {estateTotalPages} 頁（共 {filteredEstates.length} 筆）
                </span>
                <button
                  disabled={estatePage >= estateTotalPages}
                  onClick={() => setEstatePage((p) => p + 1)}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                >
                  下一頁
                </button>
              </div>
            )}
          </div>
        )}
        </>
        )}

        {/* ===== 自選屋苑 Tab ===== */}
        {activeTab === 'custom' && (
          <>
            {success && (
              <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
                {success}
              </div>
            )}
            {customError && (
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
                {customError}
              </div>
            )}

            {showCustomForm && (
              <div className="mb-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4">
                  {customEditingId ? '編輯自選屋苑' : '新增自選屋苑'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">屋苑名稱 *</label>
                    <input
                      type="text"
                      value={customFormData.name}
                      onChange={e => setCustomFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="例如：Yoho Midtown"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">地區 *</label>
                    <select
                      value={customFormData.region}
                      onChange={e => setCustomFormData(prev => ({ ...prev, region: e.target.value as 'NT' | 'KL' | 'HK' }))}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      {regionOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">行政區域 *</label>
                    <input
                      type="text"
                      value={customFormData.district}
                      onChange={e => setCustomFormData(prev => ({ ...prev, district: e.target.value, region: deriveRegionFromDistrict(e.target.value) }))}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="例如：北區、元朗區"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">分區</label>
                    <input
                      type="text"
                      value={customFormData.area}
                      onChange={e => setCustomFormData(prev => ({ ...prev, area: e.target.value }))}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="例如：元朗"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">屋苑類型</label>
                    <input
                      type="text"
                      value={customFormData.estateType}
                      onChange={e => setCustomFormData(prev => ({ ...prev, estateType: e.target.value }))}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="例如：公屋、居屋、私人屋苑"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">最低消費（HK$）</label>
                    <input
                      type="number"
                      min={0}
                      value={customFormData.minOrderAmount}
                      onChange={e => setCustomFormData(prev => ({ ...prev, minOrderAmount: e.target.value }))}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="0 代表不設最低消費"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序</label>
                    <input
                      type="number"
                      value={customFormData.sortOrder}
                      onChange={e => setCustomFormData(prev => ({ ...prev, sortOrder: e.target.value }))}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div className="flex items-center gap-2 md:col-span-2">
                    <input
                      id="custom-estate-active"
                      type="checkbox"
                      checked={customFormData.isActive}
                      onChange={e => setCustomFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                    />
                    <label htmlFor="custom-estate-active" className="text-sm text-gray-700 dark:text-gray-300">啟用</label>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleCustomSubmit}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg transition"
                  >
                    <Save size={18} /> 儲存
                  </button>
                  <button
                    onClick={resetCustomForm}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-bold rounded-lg transition"
                  >
                    <X size={18} /> 取消
                  </button>
                </div>
              </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex flex-col lg:flex-row lg:items-end gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 flex-1">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">港九新界</label>
                    <select
                      value={customRegionFilter}
                      onChange={(e) => {
                        setCustomRegionFilter(e.target.value as 'all' | 'HK' | 'KL' | 'NT');
                        setCustomDistrictFilter('all');
                        setCustomAreaFilter('all');
                      }}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    >
                      <option value="all">全部</option>
                      {regionOptions.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">行政區域</label>
                    <select
                      value={customDistrictFilter}
                      onChange={(e) => {
                        setCustomDistrictFilter(e.target.value);
                        setCustomAreaFilter('all');
                      }}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    >
                      {customDistrictOptions.map((d) => (
                        <option key={d} value={d === '全部' ? 'all' : d}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">具體地區</label>
                    <select
                      value={customAreaFilter}
                      onChange={(e) => setCustomAreaFilter(e.target.value)}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    >
                      {customAreaOptions.map((a) => (
                        <option key={a} value={a === '全部' ? 'all' : a}>{a}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">屋苑類型</label>
                    <select
                      value={customTypeFilter}
                      onChange={(e) => setCustomTypeFilter(e.target.value)}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    >
                      {customTypeOptions.map((t) => (
                        <option key={t} value={t === '全部' ? 'all' : t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">搜尋屋苑</label>
                    <div className="relative">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="輸入屋苑名稱"
                        value={customSearch}
                        onChange={(e) => setCustomSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {customLoading ? (
              <p className="text-gray-600 dark:text-gray-400">載入中...</p>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">名稱</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">港九新界</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">行政區域</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">具體地區</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">類型</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">最低消費</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">狀態</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">排序</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">操作</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {paginatedCustomEstates.map(estate => (
                        <tr key={estate.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">{estate.name}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                            {estate.region === 'HK' ? '香港島' : estate.region === 'KL' ? '九龍' : '新界'}
                          </td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{estate.district || '-'}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{estate.area || '-'}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{estate.estateType || '-'}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                            {estate.minOrderAmount > 0 ? `HK$${estate.minOrderAmount}` : '不設最低消費'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${(estate.isActive === 1 || estate.isActive === true) ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                              {(estate.isActive === 1 || estate.isActive === true) ? '啟用' : '停用'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{estate.sortOrder}</td>
                          <td className="px-4 py-3 text-right">
                            {canWriteEstates ? (
                              <div className="inline-flex gap-2">
                                <button
                                  onClick={() => handleCustomEdit(estate)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition"
                                  title="編輯"
                                >
                                  <Edit2 size={18} />
                                </button>
                                <button
                                  onClick={() => handleCustomDelete(estate.id)}
                                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
                                  title="刪除"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                      {paginatedCustomEstates.length === 0 && (
                        <tr>
                          <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                            暫時沒有自選屋苑，請按「新增自選屋苑」建立。
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {filteredCustomEstates.length > CUSTOM_PER_PAGE && (
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-center items-center gap-3">
                    <button
                      disabled={customPage <= 1}
                      onClick={() => setCustomPage((p) => p - 1)}
                      className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                    >
                      上一頁
                    </button>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      第 {customPage} / {customTotalPages} 頁（共 {filteredCustomEstates.length} 筆）
                    </span>
                    <button
                      disabled={customPage >= customTotalPages}
                      onClick={() => setCustomPage((p) => p + 1)}
                      className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                    >
                      下一頁
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ===== 需求池 Tab ===== */}
        {activeTab === 'demand' && (
          <>
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-300 text-sm">
              開通新屋苑後，可於「客戶清單」以標籤「屋苑需求：{'{屋苑名}'}」篩選登記客，再發送廣播通知。
            </div>

            {demandError && (
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
                {demandError}
              </div>
            )}

            <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">登記類型篩選</label>
              <select
                value={demandTypeFilter}
                onChange={(e) => setDemandTypeFilter(e.target.value as typeof demandTypeFilter)}
                className="sm:w-72 p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                {REQUESTER_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {demandLoading ? (
              <p className="text-gray-600 dark:text-gray-400">載入中...</p>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">屋苑名稱</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">地區</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">登記類型</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">登記人數</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">最新登記</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">操作</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredDemandGroups.map(group => {
                        const groupKey = `${group.estateName}||${group.district}||${group.requesterType}`;
                        return (
                        <React.Fragment key={groupKey}>
                          <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">{group.estateName}</td>
                            <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{group.district}</td>
                            <td className="px-4 py-3">
                              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                {requesterTypeLabel(group.requesterType)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                                {group.count}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                              {formatEpochDate(group.latestAt)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => setExpandedEstate(expandedEstate === groupKey ? null : groupKey)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition font-medium"
                              >
                                {expandedEstate === groupKey ? '收合' : '查看'}
                              </button>
                            </td>
                          </tr>
                          {expandedEstate === groupKey && (
                            <tr>
                              <td colSpan={6} className="px-4 py-4 bg-gray-50 dark:bg-gray-900/40">
                                <table className="min-w-full text-sm">
                                  <thead>
                                    <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                                      <th className="pb-2 pr-4 font-semibold">姓名</th>
                                      <th className="pb-2 pr-4 font-semibold">電話</th>
                                      <th className="pb-2 font-semibold">登記時間</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {group.entries.map((entry, i) => (
                                      <tr key={i}>
                                        <td className="py-2 pr-4 text-gray-900 dark:text-white">{entry.name || '-'}</td>
                                        <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{entry.phone}</td>
                                        <td className="py-2 text-gray-700 dark:text-gray-300">
                                          {formatEpochDate(entry.createdAt)}
                                        </td>
                                      </tr>
                                    ))}
                                    {group.entries.length === 0 && (
                                      <tr>
                                        <td colSpan={3} className="py-3 text-center text-gray-500 dark:text-gray-400">暫無登記記錄</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )})}
                      {filteredDemandGroups.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                            暫時未有屋苑需求登記
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminEstates;
